/**
 * Email Detail Page
 *
 * View email details including HTML body content
 * Similar design to VendorManagement view page
 */

import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  IconArrowLeft,
  IconMail,
  IconUser,
  IconUsers,
  IconClock,
  IconSend,
  IconCheck,
  IconX,
  IconRefresh,
  IconAlertCircle,
  IconCalendar,
  IconFileText,
  IconEdit,
} from '@tabler/icons-react';
import { LayoutContainer } from '@/components/ui/layout';
import {
  Stack,
  Card,
  Group,
  Button,
  Alert,
  Text,
  Grid,
  Badge,
  Tabs,
  Title,
  ThemeIcon,
  Skeleton,
  Paper,
  Divider,
  Code,
  ScrollArea,
} from '@/components/ui';
import { FadeIn } from '@/components/animations';
import { StatusTracker } from '@/components/advanced/workflow';
import { alert } from '@/components/ui/providers/AlertProvider';
import { useEmailCommunications } from '../../hooks/useEmailCommunications';
import { HtmlEmailPreview } from '../../../_shared/HtmlEmailPreview';
import { formatDateTime, PRIORITIES } from '../../../types';
import type { EmailApiResponse } from '../../types';

export default function EmailDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const emailId = Number(id);

  const {
    loading,
    fetchQueueItem,
    retryFailedItem,
    updatePriority,
  } = useEmailCommunications();

  const [email, setEmail] = useState<EmailApiResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<string | null>('details');

  // Fetch email data
  useEffect(() => {
    const loadEmail = async () => {
      if (isNaN(emailId)) {
        setError('Invalid email ID');
        return;
      }

      try {
        const data = await fetchQueueItem(emailId);
        setEmail(data);
      } catch (err: any) {
        setError(err?.message || 'Failed to load email');
      }
    };

    loadEmail();
  }, [emailId, fetchQueueItem]);

  // Decode base64 body
  const decodedBody = useMemo(() => {
    if (!email?.body) return '';
    try {
      return atob(email.body);
    } catch {
      return email.body;
    }
  }, [email?.body]);

  // Handle retry
  const handleRetry = async () => {
    const confirmed = await alert.confirm(
      'Retry Email',
      `Are you sure you want to retry sending this email to ${email?.recipient}?`
    );

    if (confirmed) {
      await retryFailedItem(emailId);
      // Refresh the data
      const data = await fetchQueueItem(emailId);
      setEmail(data);
    }
  };

  // Handle edit (for pending emails)
  const handleEdit = () => {
    navigate(`/admin/communications/email?edit=${emailId}`);
  };

  // Status color helper using boolean fields
  const getStatusColor = (emailData: EmailApiResponse | null) => {
    if (!emailData) return 'gray';
    if (emailData.hasError) return 'red';
    if (emailData.isSent) return 'green';
    if (emailData.isScheduled) return 'blue';
    if (emailData.isPending) return 'yellow';
    return 'gray';
  };

  // Status icon helper
  const getStatusIcon = (emailData: EmailApiResponse | null) => {
    if (!emailData) return <IconMail size={16} />;
    if (emailData.hasError) return <IconX size={16} />;
    if (emailData.isSent) return <IconCheck size={16} />;
    if (emailData.isScheduled) return <IconCalendar size={16} />;
    if (emailData.isPending) return <IconClock size={16} />;
    return <IconMail size={16} />;
  };

  // Define status tracker steps
  const statusSteps = useMemo(() => {
    if (!email) return [];

    // Parse createdDateTime (yyyyMMddHHmmss format)
    const parseDateTime = (dt: number | string | null) => {
      if (!dt) return undefined;
      const str = String(dt);
      if (str.length >= 12) {
        return new Date(
          parseInt(str.substring(0, 4)),
          parseInt(str.substring(4, 6)) - 1,
          parseInt(str.substring(6, 8)),
          parseInt(str.substring(8, 10)),
          parseInt(str.substring(10, 12))
        );
      }
      return new Date(dt);
    };

    return [
      {
        key: 'queued',
        label: 'Queued',
        description: 'Added to queue',
        timestamp: parseDateTime(email.createdDateTime),
      },
      {
        key: 'scheduled',
        label: 'Scheduled',
        description: email.scheduledFor ? `Scheduled for ${email.scheduledFor}` : 'Ready to send',
        timestamp: undefined,
      },
      {
        key: 'processing',
        label: 'Processing',
        description: 'Being processed',
        timestamp: undefined,
      },
      {
        key: 'sent',
        label: 'Sent',
        description: 'Delivered to recipient',
        timestamp: email.sentDate ? new Date(email.sentDate) : undefined,
      },
    ];
  }, [email]);

  // Get completed steps based on status
  const completedSteps = useMemo(() => {
    if (!email) return [];

    const steps: string[] = ['queued'];

    // If scheduled, mark scheduled as complete
    if (email.isScheduled || email.isPending || email.isSent) {
      steps.push('scheduled');
    }

    // If sent or no longer pending, mark processing as complete
    if (email.isSent) {
      steps.push('processing');
      steps.push('sent');
    }

    return steps;
  }, [email]);

  // Loading state
  if (loading && !email) {
    return (
      <LayoutContainer>
        <Stack gap="lg">
          <Group>
            <Skeleton height={40} width={40} circle />
            <Stack gap={4}>
              <Skeleton height={24} width={300} />
              <Skeleton height={16} width={200} />
            </Stack>
          </Group>
          <Skeleton height={200} />
          <Skeleton height={400} />
        </Stack>
      </LayoutContainer>
    );
  }

  // Error state
  if (error || !email) {
    return (
      <LayoutContainer>
        <Stack gap="lg">
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate('/admin/communications/email')}
          >
            Back to Email Queue
          </Button>
          <Alert color="red" title="Error" icon={<IconAlertCircle size={16} />}>
            {error || 'Email not found'}
          </Alert>
        </Stack>
      </LayoutContainer>
    );
  }

  return (
    <LayoutContainer>
      <FadeIn>
        <Stack gap="lg">
          {/* Back button */}
          <Button
            variant="subtle"
            leftSection={<IconArrowLeft size={16} />}
            onClick={() => navigate('/admin/communications/email')}
            style={{ alignSelf: 'flex-start' }}
          >
            Back to Email Queue
          </Button>

          {/* Header Card */}
          <Card withBorder padding="lg" radius="md">
            <Group justify="space-between" wrap="wrap">
              <Group gap="md">
                <ThemeIcon
                  size="xl"
                  radius="xl"
                  color={getStatusColor(email)}
                  variant="light"
                >
                  <IconMail size={24} />
                </ThemeIcon>
                <Stack gap={4}>
                  <Group gap="sm">
                    <Title order={3} lineClamp={1} style={{ maxWidth: 500 }}>
                      {email.subject || '(No Subject)'}
                    </Title>
                    <Badge
                      variant="light"
                      color={email.deliveryStatusColor || getStatusColor(email)}
                      size="lg"
                      leftSection={getStatusIcon(email)}
                    >
                      {email.deliveryStatus}
                    </Badge>
                  </Group>
                  <Group gap="md">
                    <Text size="sm" c="dimmed">
                      ID: #{email.emailID}
                    </Text>
                    <Text size="sm" c="dimmed">
                      Priority:{' '}
                      <Badge variant="outline" size="sm" color={email.effectivePriorityColor || undefined}>
                        {email.priorityName || email.priority}
                      </Badge>
                    </Text>
                    {email.timeDisplay && (
                      <Text size="sm" c="dimmed">
                        {email.timeDisplay}
                      </Text>
                    )}
                  </Group>
                </Stack>
              </Group>

              <Group>
                {email.hasError && (
                  <Button
                    variant="light"
                    color="green"
                    leftSection={<IconRefresh size={16} />}
                    onClick={handleRetry}
                  >
                    Retry
                  </Button>
                )}
                {email.isPending && (
                  <Button
                    variant="light"
                    color="blue"
                    leftSection={<IconEdit size={16} />}
                    onClick={handleEdit}
                  >
                    Edit
                  </Button>
                )}
              </Group>
            </Group>

            <Divider my="md" />

            {/* Quick Info */}
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <Group gap="sm">
                  <ThemeIcon size="md" variant="light" color="blue">
                    <IconUser size={16} />
                  </ThemeIcon>
                  <Stack gap={0}>
                    <Text size="xs" c="dimmed">
                      To
                    </Text>
                    <Text size="sm" fw={500}>
                      {email.displayName || email.recipient || 'Not provided'}
                    </Text>
                  </Stack>
                </Group>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <Group gap="sm">
                  <ThemeIcon size="md" variant="light" color="green">
                    <IconSend size={16} />
                  </ThemeIcon>
                  <Stack gap={0}>
                    <Text size="xs" c="dimmed">
                      From
                    </Text>
                    <Text size="sm" fw={500}>
                      {email.from || 'System'}
                    </Text>
                  </Stack>
                </Group>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <Group gap="sm">
                  <ThemeIcon size="md" variant="light" color="cyan">
                    <IconClock size={16} />
                  </ThemeIcon>
                  <Stack gap={0}>
                    <Text size="xs" c="dimmed">
                      Queued
                    </Text>
                    <Text size="sm" fw={500}>
                      {formatDateTime(email.createdDateTime)}
                    </Text>
                  </Stack>
                </Group>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
                <Group gap="sm">
                  <ThemeIcon size="md" variant="light" color="orange">
                    <IconCalendar size={16} />
                  </ThemeIcon>
                  <Stack gap={0}>
                    <Text size="xs" c="dimmed">
                      Scheduled
                    </Text>
                    <Text size="sm" fw={500}>
                      {email.scheduledFor || 'Immediate'}
                    </Text>
                  </Stack>
                </Group>
              </Grid.Col>
            </Grid>
          </Card>

          {/* Error Alert */}
          {email.hasError && (
            <Alert
              color="red"
              icon={<IconAlertCircle size={16} />}
              title="Delivery Failed"
            >
              {email.errorMessage || 'This email failed to send. Please check the email configuration and try again using the Retry button above.'}
            </Alert>
          )}

          {/* Tabs */}
          <Tabs value={activeTab} onChange={setActiveTab}>
            <Tabs.List>
              <Tabs.Tab value="details" leftSection={<IconFileText size={16} />}>
                Details
              </Tabs.Tab>
              <Tabs.Tab value="body" leftSection={<IconMail size={16} />}>
                Email Body
              </Tabs.Tab>
              <Tabs.Tab value="status" leftSection={<IconClock size={16} />}>
                Delivery Status
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="details" pt="md">
              <Grid>
                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Card withBorder padding="md" radius="md">
                    <Title order={5} mb="md">
                      Recipient Information
                    </Title>
                    <Stack gap="sm">
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          To
                        </Text>
                        <Text size="sm" fw={500}>
                          {email.recipient || '-'}
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          CC
                        </Text>
                        <Text size="sm" fw={500}>
                          {email.cc || '-'}
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          BCC
                        </Text>
                        <Text size="sm" fw={500}>
                          {email.bcc || '-'}
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Reply-To
                        </Text>
                        <Text size="sm" fw={500}>
                          {email.replyTo || '-'}
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          From
                        </Text>
                        <Text size="sm" fw={500}>
                          {email.from || 'System'}
                        </Text>
                      </Group>
                      {email.attachments && email.attachments.length > 0 && (
                        <Stack gap={4}>
                          <Text size="sm" c="dimmed">Attachments ({email.attachments.length})</Text>
                          {email.attachments.map((a) => (
                            <Badge key={a.fileId} variant="light" leftSection={<IconFileText size={12} />} style={{ maxWidth: '100%' }}>
                              {a.fileName || a.fileId}
                            </Badge>
                          ))}
                        </Stack>
                      )}
                      {email.totalRecipients > 1 && (
                        <Group justify="space-between">
                          <Text size="sm" c="dimmed">
                            Total Recipients
                          </Text>
                          <Badge variant="light" color="blue">
                            {email.totalRecipients}
                          </Badge>
                        </Group>
                      )}
                    </Stack>
                  </Card>
                </Grid.Col>

                <Grid.Col span={{ base: 12, md: 6 }}>
                  <Card withBorder padding="md" radius="md">
                    <Title order={5} mb="md">
                      Email Settings
                    </Title>
                    <Stack gap="sm">
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Priority
                        </Text>
                        <Badge variant="outline" color={email.effectivePriorityColor || undefined}>
                          {email.priorityName || PRIORITIES.find((p) => p.value === email.priority)?.label || email.priority}
                        </Badge>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          HTML Email
                        </Text>
                        <Badge
                          variant="light"
                          color={email.isHtml ? 'green' : 'gray'}
                        >
                          {email.isHtml ? 'Yes' : 'No'}
                        </Badge>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Scheduled
                        </Text>
                        <Text size="sm" fw={500}>
                          {email.scheduledFor || 'Immediate'}
                        </Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">
                          Created
                        </Text>
                        <Text size="sm" fw={500}>
                          {formatDateTime(email.createdDateTime)}
                        </Text>
                      </Group>
                      {email.templateName && (
                        <Group justify="space-between">
                          <Text size="sm" c="dimmed">
                            Template
                          </Text>
                          <Text size="sm" fw={500}>
                            {email.templateName}
                          </Text>
                        </Group>
                      )}
                      {email.sentDate && (
                        <Group justify="space-between">
                          <Text size="sm" c="dimmed">
                            Sent Date
                          </Text>
                          <Text size="sm" fw={500}>
                            {email.sentDate}
                          </Text>
                        </Group>
                      )}
                    </Stack>
                  </Card>
                </Grid.Col>

                <Grid.Col span={12}>
                  <Card withBorder padding="md" radius="md">
                    <Title order={5} mb="md">
                      Subject
                    </Title>
                    <Text size="sm">{email.subject || '(No Subject)'}</Text>
                  </Card>
                </Grid.Col>
              </Grid>
            </Tabs.Panel>

            <Tabs.Panel value="body" pt="md">
              <Card withBorder padding="md" radius="md">
                <Group justify="space-between" mb="md">
                  <Title order={5}>Email Content</Title>
                  <Badge variant="light" color={email.isHtml ? 'blue' : 'gray'}>
                    {email.isHtml ? 'HTML' : 'Plain Text'}
                  </Badge>
                </Group>

                {email.isHtml ? (
                  <HtmlEmailPreview html={decodedBody} height={600} />
                ) : (
                  <Paper withBorder p="md" radius="md">
                    <ScrollArea h={400} type="auto">
                      <Text
                        style={{
                          whiteSpace: 'pre-wrap',
                          fontFamily: 'monospace',
                        }}
                      >
                        {decodedBody}
                      </Text>
                    </ScrollArea>
                  </Paper>
                )}

                {/* Raw HTML source toggle */}
                {email.isHtml && (
                  <details style={{ marginTop: '16px' }}>
                    <summary
                      style={{ cursor: 'pointer', marginBottom: '8px' }}
                    >
                      <Text span size="sm" c="dimmed">
                        View HTML Source
                      </Text>
                    </summary>
                    <Paper withBorder p="md" radius="md" bg="gray.0">
                      <ScrollArea h={300} type="auto">
                        <Code block style={{ whiteSpace: 'pre-wrap' }}>
                          {decodedBody}
                        </Code>
                      </ScrollArea>
                    </Paper>
                  </details>
                )}
              </Card>
            </Tabs.Panel>

            <Tabs.Panel value="status" pt="md">
              <Card withBorder padding="md" radius="md">
                <Title order={5} mb="md">
                  Delivery Timeline
                </Title>

                <StatusTracker
                  title=""
                  variant="timeline"
                  steps={statusSteps}
                  currentStatus={email.deliveryStatus?.toLowerCase() || 'queued'}
                  completedSteps={completedSteps}
                  failedStep={email.hasError ? 'processing' : undefined}
                  showTimestamps
                  orientation="vertical"
                />

                {email.hasError && (
                  <Alert
                    mt="md"
                    color="red"
                    icon={<IconAlertCircle size={16} />}
                    title="Delivery Failed"
                  >
                    {email.errorMessage || 'The email failed to deliver. This could be due to invalid email address, mail server issues, or configuration problems. Use the Retry button to attempt sending again.'}
                  </Alert>
                )}

                {email.isSent && !email.hasError && (
                  <Alert
                    mt="md"
                    color="green"
                    icon={<IconCheck size={16} />}
                    title="Successfully Delivered"
                  >
                    The email was successfully delivered to the recipient's mail server.
                    {email.sentDate && ` Sent on: ${email.sentDate}`}
                  </Alert>
                )}

                {/* SES delivery tracking (from configuration-set events) */}
                {(email.deliveredDateTime || email.openedDateTime || email.clickedDateTime || email.bounceType || email.unsubscribeDateTime) && (
                  <Card withBorder padding="md" radius="md" mt="md">
                    <Title order={6} mb="sm">SES Tracking</Title>
                    <Stack gap="xs">
                      {email.deliveredDateTime && (
                        <Group justify="space-between"><Badge color="green" variant="light">Delivered</Badge><Text size="sm" c="dimmed">{formatDateTime(email.deliveredDateTime as any)}</Text></Group>
                      )}
                      {(email.openedDateTime || email.readDateTime) && (
                        <Group justify="space-between"><Badge color="green" variant="light">Opened</Badge><Text size="sm" c="dimmed">{formatDateTime((email.openedDateTime || email.readDateTime) as any)}</Text></Group>
                      )}
                      {email.clickedDateTime && (
                        <Group justify="space-between"><Badge color="teal" variant="light">Clicked a link</Badge><Text size="sm" c="dimmed">{formatDateTime(email.clickedDateTime as any)}</Text></Group>
                      )}
                      {email.bounceType && (
                        <Group justify="space-between"><Badge color="red" variant="light">Bounced ({email.bounceType})</Badge><Text size="sm" c="dimmed">delivery failed</Text></Group>
                      )}
                      {email.unsubscribeDateTime && (
                        <Group justify="space-between"><Badge color="grape" variant="light">Complaint / Unsubscribed</Badge><Text size="sm" c="dimmed">{formatDateTime(email.unsubscribeDateTime as any)}</Text></Group>
                      )}
                    </Stack>
                  </Card>
                )}

                {email.isPending && (
                  <Alert
                    mt="md"
                    color="yellow"
                    icon={<IconClock size={16} />}
                    title="Pending"
                  >
                    This email is queued and waiting to be processed.
                    {email.isScheduled && email.scheduledFor && ` Scheduled for: ${email.scheduledFor}`}
                  </Alert>
                )}
              </Card>
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </FadeIn>
    </LayoutContainer>
  );
}
